import React, { useState, useEffect } from 'react';
import ErpiaSelect from './ErpiaSelect';
import APIService from '../services/api';

// Le jeton se LIT au moment de l'appel, jamais reçu en prop : la session
// glissante le remplace en cours de route, et un jeton capturé au rendu finit
// par mourir avant la session (vécu 27.09).
const jeton = () => APIService.getToken();

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, className = '' }) => <i className={`bi bi-${icon} ${className}`} />;

// ── Modal planification d'un appel ──────────────────────────────────────────
export function ScheduleCallModal({ candidat, besoin, questions, onClose, onScheduled }) {
  const [phone, setPhone] = useState(candidat.telephone || candidat.whatsapp || '');
  const [scheduledAt, setScheduledAt] = useState('');
  const [language, setLanguage] = useState('fr');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Min = maintenant + 2 min
  const minDatetime = new Date(Date.now() + 2 * 60000).toISOString().slice(0, 16);

  const handleSubmit = async () => {
    if (!phone.trim()) return setError('Numéro de téléphone requis');
    if (!scheduledAt) return setError('Date et heure requises');
    if (!questions?.length) return setError('Aucune question configurée pour ce besoin');

    setLoading(true); setError('');
    try {
      const res = await fetch(`${BASE}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jeton()}` },
        body: JSON.stringify({
          candidat_id: candidat.id,
          besoin_id: besoin.id,
          candidat_nom: `${candidat.prenom || ''} ${candidat.nom}`.trim(),
          titre_besoin: besoin.titre,
          phone: phone.trim(),
          questions,
          language,
          scheduled_at: new Date(scheduledAt).toISOString()
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      onScheduled(data.data);
      onClose();
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const lab = { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-3)', marginBottom: '7px' };
  const inp = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: '13.5px', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400, padding: '16px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel, var(--surface))', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '460px', overflow: 'hidden' }}>
        {/* En-tête */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--hair)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
            <BI icon="calendar2-event" />
            <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)' }}>Planifier un appel IA</h2>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: '6px', borderRadius: '7px', fontSize: '14px' }}><BI icon="x-lg" /></button>
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Candidat */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '11px 13px', borderRadius: '10px', background: 'var(--ghost-bg)', border: '1px solid var(--border)' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: 'var(--accent-soft-bg, var(--chip-bg))', color: 'var(--text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '14px', fontFamily: "'IBM Plex Mono', monospace", flex: 'none' }}>
              {(candidat.prenom || candidat.nom || '?')[0].toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{candidat.prenom} {candidat.nom}</p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-2)' }}>{besoin.titre}{besoin.client ? ` · ${besoin.client}` : ''}</p>
            </div>
          </div>

          {/* Téléphone */}
          <div>
            <label style={lab}><BI icon="telephone" /> Numéro de téléphone</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+41 79 000 00 00" style={inp} />
          </div>

          {/* Date/heure */}
          <div>
            <label style={lab}><BI icon="clock" /> Date et heure de l'appel</label>
            <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} min={minDatetime} style={inp} />
          </div>

          {/* Langue */}
          <div>
            <label style={lab}><BI icon="translate" /> Langue de l'appel</label>
            <ErpiaSelect value={language} onChange={e => setLanguage(e.target.value)} style={inp}>
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </ErpiaSelect>
          </div>

          {/* Questions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 13px', borderRadius: '9px', background: 'var(--ghost-bg)', fontSize: '12.5px', color: 'var(--text-2)' }}>
            <BI icon="question-circle" /> {questions?.length || 0} question(s) configurée(s) pour ce besoin
          </div>

          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 13px', borderRadius: '9px', background: 'var(--danger-bg, rgba(207,59,50,0.1))', color: 'var(--danger-fg, #cf3b32)', fontSize: '12.5px', fontWeight: 500 }}>
              <BI icon="exclamation-triangle" /> {error}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', padding: '0 20px 18px', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>Annuler</button>
          <button onClick={handleSubmit} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 18px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1 }}>
            <BI icon={loading ? 'arrow-repeat' : 'calendar2-check'} /> {loading ? 'Planification…' : 'Planifier l\'appel'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Liste des appels planifiés pour un besoin ────────────────────────────────
export function ScheduledCallsList({ besoinId, refreshKey }) {
  const [calls, setCalls] = useState([]);

  useEffect(() => {
    fetch(`${BASE}/schedule/besoin/${besoinId}`, {
      headers: { 'Authorization': `Bearer ${jeton()}` }
    }).then(r => r.json()).then(data => {
      if (data.success) setCalls(data.data);
    }).catch(() => {});
  }, [besoinId, refreshKey]);

  const cancel = async (id) => {
    await fetch(`${BASE}/schedule/${id}/cancel`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${jeton()}` }
    });
    setCalls(prev => prev.map(c => c.id === id ? {...c, status: 'cancelled'} : c));
  };

  if (!calls.length) return null;

  const statusConfig = {
    pending:   { color: 'bg-amber-100 text-amber-700',  icon: 'clock-fill',        label: 'Planifié' },
    running:   { color: 'bg-blue-100 text-blue-700',    icon: 'arrow-repeat',      label: 'En cours' },
    done:      { color: 'bg-green-100 text-green-700',  icon: 'check-circle-fill', label: 'Effectué' },
    failed:    { color: 'bg-red-100 text-red-700',      icon: 'x-circle-fill',     label: 'Échoué' },
    cancelled: { color: 'bg-gray-100 text-gray-500',    icon: 'slash-circle-fill', label: 'Annulé' },
  };

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 18px', borderBottom: '1px solid var(--hair)' }}>
        <BI icon="calendar2-event" />
        <h2 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}>Appels planifiés</h2>
        <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: 'var(--chip-bg)', color: 'var(--text-2)' }}>
          {calls.filter(c => c.status === 'pending').length} en attente
        </span>
      </div>
      <div>
        {calls.map((call, ci) => {
          const cfg = statusConfig[call.status] || statusConfig.pending;
          const date = new Date(call.scheduled_at);
          return (
            <div key={call.id} className="flex items-center justify-between gap-3" style={{ padding: '11px 18px', borderTop: ci === 0 ? 'none' : '1px solid var(--hair)' }}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex items-center justify-center flex-shrink-0" style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--chip-bg)', color: 'var(--text-strong)', fontWeight: 600, fontSize: '12px', fontFamily: "'IBM Plex Mono', monospace" }}>
                  {(call.candidat_nom || '?')[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="truncate" style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{call.candidat_nom}</p>
                  <div className="flex items-center gap-2" style={{ fontSize: '11.5px', color: 'var(--text-2)', marginTop: '2px' }}>
                    <BI icon="telephone" />
                    {call.phone}
                    <span>·</span>
                    <BI icon="clock" />
                    {date.toLocaleDateString('fr-FR')} à {date.toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}
                  </div>
                  {call.error_msg && (
                    <p className="truncate" style={{ fontSize: '11.5px', color: 'var(--danger-fg, #cf3b32)', marginTop: '2px' }}>{call.error_msg}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${cfg.color}`}>
                  <BI icon={cfg.icon} /> {cfg.label}
                </span>
                {call.status === 'pending' && (
                  <button onClick={() => cancel(call.id)}
                    className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg transition">
                    Annuler
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ScheduleCallModal;
